import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const currentCategory = categories.find(c => c.slug === currentSlug) || categories[0];

  const filteredComics = useMemo(() => getComicsByCategory(currentSlug), [currentSlug]);
  const [activeSlug, setActiveSlug] = useState(currentSlug);

  useEffect(() => {
    setActiveSlug(currentSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  const categoryKeywords = currentCategory.slug === 'all'
    ? '韩国漫画,无删减漫画,下拉式漫画,免费韩漫,韩漫在线阅读,韩漫网址'
    : `韩国漫画,${currentCategory.name}韩漫,无删减漫画,下拉式漫画,免费韩漫在线阅读`;

  const categoryTitle = currentCategory.slug === 'all'
    ? '全部韩国漫画 - 免费韩漫在线阅读 - 韩漫网址'
    : `${currentCategory.name}韩国漫画 - 免费${currentCategory.name}韩漫在线阅读 - 韩漫网址`;

  const categoryDesc = currentCategory.slug === 'all'
    ? '韩漫网址提供海量优质韩国漫画资源，覆盖恋爱、奇幻、动作、悬疑、喜剧、剧情等多样题材。全部无删减下拉式阅读，免费韩漫在线畅览。'
    : `韩漫网址为您精选${currentCategory.name}类韩国漫画，无删减下拉式阅读体验，全部免费在线阅读。发现最受欢迎的${currentCategory.name}韩漫作品，畅享优质漫画内容。`;

  return (
    <>
      <SEOHead
        title={categoryTitle}
        description={categoryDesc}
        keywords={categoryKeywords}
        canonicalUrl={`https://hanman.example.com/category/${currentSlug}`}
      />
      <SchemaOrg type="CollectionPage" data={{
        name: currentCategory.name === '全部' ? '全部韩国漫画' : `${currentCategory.name}韩国漫画`,
        description: categoryDesc,
        url: `https://hanman.example.com/category/${currentSlug}`,
        isPartOf: { '@type': 'WebSite', name: '韩漫网址', url: 'https://hanman.example.com/' },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name === '全部' ? '全部漫画' : `${currentCategory.name}韩漫` },
              ]} />
              <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                {currentCategory.name === '全部' ? '全部韩国漫画' : `${currentCategory.name}韩国漫画`}
              </h1>
              <p className="mt-2 text-sm text-foreground-600">{categoryDesc}</p>
            </div>
          </div>

          <div className="sticky top-16 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-800'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <section className="py-8 md:py-12 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {filteredComics.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-foreground-600">
                      共找到 <strong className="text-foreground-900">{filteredComics.length}</strong> 部漫画
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {filteredComics.map(comic => (
                      <ComicCard key={comic.id} comic={comic} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mx-auto mb-4">
                    <i className="ri-book-2-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无漫画，敬请期待新作品上线</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}